/**
 * MAS Direct template content - Direct-only JSX and logic.
 * Isolated from MAS Indirect: GSMS NDC, Target Launch Date, WAC Analysis, isDirect sections.
 */
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import {
  formatDateForChartAPI,
  parseDateString,
  preserveGlobalFields as preserveGlobalFieldsLogic,
} from '../../bidAnalysisTemplate.logic';
import classes from '../../bidAnalysisTemplate.module.scss';
import { MASFormContent } from '../../components/content/MASFormContent';
import type { MASTemplateContentCtx } from '../../components/content/MASTemplateContent';
import {
  CogsBuildupSection,
  CommercialPriceRankingSection,
  CompetitiveAnalysisSection,
  ContractPriceRankingSection,
  CurrentMarketStateSection,
  FssTrackingSection,
  PackSizeOutputSection,
  PricingCalculatorSection,
  TmopCalculatorSection,
  WACAnalysisSection,
} from '../../components/sections';
import { MASDirectGlobalOutputSection } from '../../components/sections/MASDirectGlobalOutputSection';
import { SECTION_NAMES } from '../../constants/sectionNames';
import { formatNDC } from '../../shared/utils/formatNDC';

export function MASDirectTemplateContent({ ctx }: { ctx: MASTemplateContentCtx }) {
  const {
    sectionRefs,
    expandedSections,
    toggleSection,
    templateData,
    formData,
    activePackSizeTab,
    analysisType,
    gsmsHndlgType,
    supplierName,
    validationErrors,
    handleFieldChange,
    handleFieldBlur,
    handleFieldKeyDown,
    setFormData,
    setPackSizeFormData,
    notes,
    handleNotesClick,
    selectedConcept,
    setSelectedConcept,
    concepts,
    packSizeTabs,
    handlePackSizeTabChange,
    SECTION_NAMES: _SECTION_NAMES_CTX,
    bidDetails,
    templateType,
    bidId,
    packSizeFormData,
    ndcOptions,
    ndcValidated,
    setNdcValidated,
    isLoadingNdcData,
    setIsLoadingNdcData,
    isValidatingNdc,
    setIsValidatingNdc,
    chartDateRange,
    tempDateRange,
    calendarViewDates,
    selectedCompany,
    isLoadingMarketTrends,
    isLoadingMarketShare,
    volumeByCot,
    isLoadingVolumeByCot,
    marketTrendsOptions,
    handleDateRangeChange,
    handleApplyDateFilter,
    setCalendarViewDates,
    handleMarketShareClick,
    setShowChartModal,
    contractPriceRanking,
    expandedRows,
    handleExpandedRowsChange,
    isLoadingContractPriceRanking,
    loadNotes,
    loadContractPriceRanking,
    loadMarketSalesTrendData,
    loadMarketShareByVolume,
    getCurrentUser,
    setChartDateRange,
    handleTempDateRangeChange: _handleTempDateRangeChange,
    hasLoadedInitialData,
    setContractPriceRanking: _setContractPriceRanking,
    replicateAcrossPackSizes,
    setActivePackSizeTab,
    bidOwner,
    formatTargetLaunchDateForDisplay,
  } = ctx;

  const preserveGlobalFields = (newData: any): any => preserveGlobalFieldsLogic(newData, formData);
  const clearedGsmsNdcForEmptyOptionsRef = useRef(false);
  const conceptDropdownRef = useRef<any>(null);
  const ndcDropdownRef = useRef<any>(null);

  // When there is no supplier bulk NDC available, clear GSMS NDC once if it matches the supplier bulk value (was auto-populated)
  useEffect(() => {
    if (ndcOptions.length > 0) {
      clearedGsmsNdcForEmptyOptionsRef.current = false;
      return;
    }
    if (clearedGsmsNdcForEmptyOptionsRef.current) return;
    const current =
      activePackSizeTab !== null ? packSizeFormData[activePackSizeTab] || formData : formData;
    const gsmsNdc = current?.gsms_ndc != null ? String(current.gsms_ndc).trim() : '';
    const supplierBulk = [current?.selectedMfgNdc, current?.mfg_ndc].find(
      v => v != null && String(v).trim() !== ''
    );
    const supplierBulkStr = supplierBulk != null ? String(supplierBulk).trim() : '';
    if (gsmsNdc === '' || gsmsNdc !== supplierBulkStr) return;
    clearedGsmsNdcForEmptyOptionsRef.current = true;
    setFormData((prev: any) => ({ ...prev, gsms_ndc: '' }));
    if (activePackSizeTab !== null) {
      setPackSizeFormData(prev => ({
        ...prev,
        [activePackSizeTab]: { ...(prev[activePackSizeTab] || {}), gsms_ndc: '' },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ndcOptions.length, activePackSizeTab, formData, packSizeFormData]);

  return (
    <>
      <MASFormContent ctx={ctx} />
      <div ref={el => (sectionRefs.current['product-info'] = el)} className={classes.section}>
        <div className={classes.sectionHeader} onClick={() => toggleSection('productDetails')}>
          <h2>Product Details</h2>
          <Button
            icon="pi pi-comment"
            className={classes.notesButton}
            onClick={e => {
              e.stopPropagation();
              handleNotesClick(SECTION_NAMES.PRODUCT_DETAILS, e);
            }}
            label={`Notes (${notes.filter(n => n.section_name === SECTION_NAMES.PRODUCT_DETAILS).length})`}
            text
            size="small"
          />
          <i
            className={`pi ${expandedSections.productDetails ? 'pi-chevron-up' : 'pi-chevron-down'}`}
          />
        </div>

        {expandedSections.productDetails && (
          <div className={classes.sectionContent}>
            <div
              className={classes.formField}
              style={{ maxWidth: '400px', marginBottom: '1.5rem' }}
            >
              <label>
                Description <span className={classes.required}>*</span>
              </label>
              <Dropdown
                ref={conceptDropdownRef}
                value={selectedConcept}
                options={concepts}
                onChange={e => {
                  conceptDropdownRef.current?.hide();
                  const concept = e.value;
                  setSelectedConcept(concept);
                  if (bidDetails?.form_details?.form_data) {
                    const conceptIndex = bidDetails.form_details.form_data.findIndex(
                      (c: any) => c.concept_name === concept
                    );
                    const conceptData = bidDetails.form_details.form_data[conceptIndex];
                    if (conceptData) {
                      const packSizes = Object.keys(conceptData.pack_size || {});
                      if (packSizes.length > 0) {
                        const tabIndex = conceptIndex * 1000 + 0;
                        if (packSizeFormData[tabIndex]) {
                          setActivePackSizeTab(tabIndex);
                          const tabData = packSizeFormData[tabIndex];
                          setFormData(
                            preserveGlobalFields({
                              ...tabData,
                              conceptName: concept,
                            })
                          );
                        } else {
                          const conceptTabs = Object.keys(packSizeFormData)
                            .map(key => parseInt(key))
                            .filter(index => Math.floor(index / 1000) === conceptIndex);
                          if (conceptTabs.length > 0) {
                            const firstTab = conceptTabs[0];
                            setActivePackSizeTab(firstTab);
                            const tabData = packSizeFormData[firstTab];
                            setFormData(
                              preserveGlobalFields({
                                ...tabData,
                                conceptName: concept,
                              })
                            );
                          }
                        }
                      }
                    }
                  }
                }}
                placeholder="Select Concept"
                filter
                style={{ width: '100%' }}
              />
            </div>

            {selectedConcept && packSizeTabs.length > 0 ? (
              <div>
                <label
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '0.5rem',
                    display: 'block',
                  }}
                >
                  Pack Sizes
                </label>
                <div className={classes.ndcTabs}>
                  {packSizeTabs.map(tab => (
                    <div
                      key={tab.index}
                      className={`${classes.ndcTab} ${activePackSizeTab === tab.index ? classes.active : ''}`}
                      onClick={() => handlePackSizeTabChange(tab.index)}
                    >
                      Pack Size {tab.packSize}
                    </div>
                  ))}
                </div>
              </div>
            ) : selectedConcept ? (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}
              >
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ fontSize: '2rem', color: '#f59e0b', marginBottom: '0.5rem' }}
                />
                <h3 style={{ color: '#92400e', marginBottom: '0.5rem' }}>
                  No Pack Sizes Available
                </h3>
                <p style={{ color: '#78350f' }}>
                  No pack sizes are available for the selected concept.
                </p>
              </div>
            ) : (
              <div
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}
              >
                <i
                  className="pi pi-info-circle"
                  style={{ fontSize: '2rem', color: '#6b7280', marginBottom: '0.5rem' }}
                />
                <p style={{ color: '#6b7280' }}>Please select a concept to view pack sizes.</p>
              </div>
            )}

            <div className={classes.mfgNdcSection}>
              <div className={classes.formField} style={{ maxWidth: '500px' }}>
                <label>
                  Supplier Bulk NDC <span className={classes.optional}>(Optional)</span>
                </label>
                <div className={classes.ndcInputGroup}>
                  <Dropdown
                    ref={ndcDropdownRef}
                    value={
                      activePackSizeTab !== null
                        ? (packSizeFormData[activePackSizeTab] || formData).selectedMfgNdc ||
                          (packSizeFormData[activePackSizeTab] || formData).mfg_ndc ||
                          ''
                        : formData.selectedMfgNdc || formData.mfg_ndc || ''
                    }
                    options={(() => {
                      const baseOptions = ndcOptions.map(ndc => ({
                        label: ndc.ndc11,
                        value: ndc.ndc11,
                      }));
                      const currentVal =
                        activePackSizeTab !== null
                          ? (packSizeFormData[activePackSizeTab] || formData).selectedMfgNdc ||
                            (packSizeFormData[activePackSizeTab] || formData).mfg_ndc ||
                            ''
                          : formData.selectedMfgNdc || formData.mfg_ndc || '';
                      const trimmed = currentVal ? String(currentVal).trim() : '';
                      if (
                        trimmed &&
                        !baseOptions.some((o: { value: string }) => o.value === trimmed)
                      ) {
                        return [{ label: trimmed, value: trimmed }, ...baseOptions];
                      }
                      return baseOptions;
                    })()}
                    onChange={async e => {
                      ndcDropdownRef.current?.hide();
                      const selectedNdc = e.value;
                      handleFieldChange('selectedMfgNdc', selectedNdc);
                      setNdcValidated(false);
                      setIsLoadingNdcData(false);
                      if (selectedNdc) {
                        const lockedData = {
                          ...formData,
                          selectedMfgNdc: selectedNdc,
                          mfg_ndc: selectedNdc,
                          gsms_mfg_ndc: null,
                          ndcValidated: false,
                        };
                        setFormData(lockedData);
                        setPackSizeFormData({
                          ...packSizeFormData,
                          [activePackSizeTab as number]: lockedData,
                        });
                      }
                    }}
                    placeholder={
                      ndcOptions.length === 0
                        ? 'No available NDC found'
                        : 'Select Supplier Bulk NDC'
                    }
                    style={{ flex: 1 }}
                    filter
                    disabled={ndcOptions.length === 0}
                  />
                  {(() => {
                    const isDisabled = isValidatingNdc || isLoadingNdcData;
                    return (
                      <Button
                        type="button"
                        label="Fetch Current Market State"
                        icon={isValidatingNdc ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                        className={classes.validateBtn}
                        loading={isValidatingNdc}
                        onClick={async () => {
                          const currentDataForClick =
                            activePackSizeTab !== null
                              ? packSizeFormData[activePackSizeTab] || formData
                              : formData;
                          const ndcToUse =
                            [
                              currentDataForClick.selectedMfgNdc,
                              currentDataForClick.mfg_ndc,
                              currentDataForClick.gsms_mfg_ndc,
                            ].find(v => v != null && String(v).trim() !== '') ?? null;
                          if (
                            !supplierName ||
                            !currentDataForClick.conceptName ||
                            !currentDataForClick.packSize
                          ) {
                            console.warn('Missing required fields for fetch');
                            return;
                          }
                          setIsValidatingNdc(true);
                          setIsLoadingNdcData(true);
                          try {
                            const productDetailResponse =
                              await bidAnalysisServices.getProductDetail(parseInt(bidId || '0'), {
                                concept_name: currentDataForClick.conceptName,
                                pack_size: parseFloat(currentDataForClick.packSize),
                                manufacturer: supplierName,
                                ...(ndcToUse ? { mfg_ndc: ndcToUse } : {}),
                              });
                            const productDetail =
                              productDetailResponse?.data || productDetailResponse;
                            if (productDetail) {
                              const updatedData = {
                                ...currentDataForClick,
                                ...(ndcToUse
                                  ? {
                                      selectedMfgNdc: ndcToUse,
                                      mfg_ndc: ndcToUse,
                                      gsms_mfg_ndc: ndcToUse,
                                      gsms_ndc:
                                        ndcOptions.length > 0
                                          ? currentDataForClick.gsms_ndc || formatNDC(ndcToUse)
                                          : (currentDataForClick.gsms_ndc ?? ''),
                                      ndcValidated: true,
                                    }
                                  : {}),
                                vaAnnualUsage: productDetail.va_annual_usage?.toString() || '0',
                                dodAnnualUsage: productDetail.dod_annual_usage?.toString() || '0',
                                usagePeriodStartDate:
                                  productDetail.start_date ||
                                  currentDataForClick.usagePeriodStartDate,
                                usagePeriodEndDate:
                                  productDetail.end_date || currentDataForClick.usagePeriodEndDate,
                                // Direct: map supplier_wac to Supplier Comparable WAC; leave wac unchanged
                                wac: currentDataForClick.wac ?? '',
                                supplier_comparable_wac:
                                  productDetail.supplier_wac != null
                                    ? productDetail.supplier_wac.toString()
                                    : (currentDataForClick.supplier_comparable_wac ?? ''),
                                supplier_wac: productDetail.supplier_wac ?? null,
                              };
                              setFormData(preserveGlobalFields(updatedData));
                              setPackSizeFormData(prev => ({
                                ...prev,
                                [activePackSizeTab as number]: updatedData,
                              }));
                              setNdcValidated(true);
                              const usageStart =
                                productDetail.start_date ||
                                currentDataForClick.usagePeriodStartDate;
                              const usageEnd =
                                productDetail.end_date || currentDataForClick.usagePeriodEndDate;
                              if (usageStart && usageEnd) {
                                try {
                                  const parsedStart = parseDateString(usageStart);
                                  const parsedEnd = parseDateString(usageEnd);
                                  if (
                                    parsedStart &&
                                    parsedEnd &&
                                    !isNaN(parsedStart.getTime()) &&
                                    !isNaN(parsedEnd.getTime())
                                  ) {
                                    setChartDateRange({
                                      startDate: parsedStart,
                                      endDate: parsedEnd,
                                    });
                                    setCalendarViewDates({
                                      startViewDate: parsedStart,
                                      endViewDate: parsedEnd,
                                    });
                                  }
                                } catch (err) {
                                  console.error(
                                    'Error setting chart date range from usage period:',
                                    err
                                  );
                                }
                              }
                              toast.info(
                                'Current market state data fetched. Current data overwritten. Please click Save to save the bid.',
                                { autoClose: 5000 }
                              );
                              if (updatedData.conceptName && updatedData.packSize) {
                                const startDate =
                                  productDetail.start_date || updatedData.usagePeriodStartDate;
                                const endDate =
                                  productDetail.end_date || updatedData.usagePeriodEndDate;
                                await loadMarketSalesTrendData(null, true);
                                await loadMarketShareByVolume(true);
                                if (startDate && endDate) {
                                  await loadContractPriceRanking(
                                    formatDateForChartAPI(startDate) || undefined,
                                    formatDateForChartAPI(endDate) || undefined,
                                    true
                                  );
                                } else {
                                  await loadContractPriceRanking(undefined, undefined, true);
                                }
                                hasLoadedInitialData.current = true;
                              }
                            }
                          } catch (err) {
                            console.error('Error fetching current market state:', err);
                            setNdcValidated(false);
                          } finally {
                            setIsValidatingNdc(false);
                            setIsLoadingNdcData(false);
                          }
                        }}
                        disabled={isDisabled}
                      />
                    );
                  })()}
                </div>
                {ndcOptions.length === 0 &&
                  supplierName &&
                  formData.conceptName &&
                  formData.packSize && (
                    <span
                      style={{
                        color: '#dc2626',
                        fontSize: '0.875rem',
                        marginTop: '0.25rem',
                        display: 'block',
                      }}
                    >
                      No available NDC found
                    </span>
                  )}
              </div>
            </div>

            {/* GSMS NDC + Target Launch Date - Direct only */}
            <>
              <div className={classes.formField} style={{ maxWidth: '500px', marginTop: '1rem' }}>
                <label>GSMS NDC</label>
                <input
                  type="text"
                  className={`p-inputtext p-component ${classes.gsmsNdcInput}`}
                  value={formData.gsms_ndc || ''}
                  onChange={e => {
                    const formatted = formatNDC(e.target.value);
                    handleFieldChange('gsms_ndc', formatted);
                  }}
                  placeholder="12345-234-23"
                  maxLength={12}
                />
              </div>
              <div className={classes.formField} style={{ maxWidth: '500px', marginTop: '1rem' }}>
                <label>Target Launch Date</label>
                <InputText
                  value={formatTargetLaunchDateForDisplay(formData.targetLaunchDate, 0)}
                  disabled
                  readOnly
                />
              </div>
            </>

            <div className={classes.packSizeContent}>
              {ndcOptions.length === 0 &&
                supplierName &&
                formData.conceptName &&
                formData.packSize && (
                  <div
                    style={{
                      padding: '1rem 1.5rem',
                      marginBottom: '1.5rem',
                      background: '#dbeafe',
                      border: '1px solid #93c5fd',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}
                  >
                    <i
                      className="pi pi-info-circle"
                      style={{ fontSize: '1.5rem', color: '#2563eb' }}
                    />
                    <p
                      style={{ color: '#1e40af', margin: 0, fontWeight: 500, fontSize: '0.95rem' }}
                    >
                      No NDC found for this molecule. You can fill the form manually.
                    </p>
                  </div>
                )}
              {ndcOptions.length > 0 &&
                !ndcValidated &&
                (formData.selectedMfgNdc || formData.mfg_ndc) && (
                  <div
                    style={{
                      padding: '1rem 1.5rem',
                      marginBottom: '1.5rem',
                      background: '#fef3c7',
                      border: '1px solid #fbbf24',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}
                  >
                    <i
                      className="pi pi-exclamation-triangle"
                      style={{ fontSize: '1.5rem', color: '#f59e0b' }}
                    />
                    <p
                      style={{ color: '#92400e', margin: 0, fontWeight: 500, fontSize: '0.95rem' }}
                    >
                      Click Fetch Current Market State to load product detail and market data.
                    </p>
                  </div>
                )}
              <div ref={el => (sectionRefs.current['current-market-state'] = el)}>
                <CurrentMarketStateSection
                  isExpanded={expandedSections.currentMarket}
                  onToggle={() => toggleSection('currentMarket')}
                  formData={formData}
                  templateData={templateData}
                  validationErrors={validationErrors}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onFieldKeyDown={handleFieldKeyDown}
                  ndcValidated={ndcValidated}
                  ndcOptions={ndcOptions}
                  templateType={templateType}
                  vaDataAsOf={bidDetails?.va_data_as_of ?? null}
                  dodDataAsOf={bidDetails?.dod_data_as_of ?? null}
                />
              </div>
              <div ref={el => (sectionRefs.current['cost-analysis'] = el)}>
                <CogsBuildupSection
                  isExpanded={expandedSections.cogsBuildUp}
                  onToggle={() => toggleSection('cogsBuildUp')}
                  formData={(() => {
                    const activeTab = packSizeTabs?.find(tab => tab.index === activePackSizeTab);
                    return activeTab?.packSize
                      ? {
                          ...formData,
                          packSize: activeTab.packSize,
                          gsmsPackSize: activeTab.packSize,
                        }
                      : formData;
                  })()}
                  isDirect={true}
                  isIndirect={false}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onFieldKeyDown={handleFieldKeyDown}
                  gsmsHndlgType={gsmsHndlgType}
                  packSizeTabs={packSizeTabs}
                  activePackSizeTab={activePackSizeTab ?? undefined}
                />
              </div>
              <div ref={el => (sectionRefs.current['pricing-strategy'] = el)}>
                <PricingCalculatorSection
                  isExpanded={expandedSections.pricingCalc}
                  onToggle={() => toggleSection('pricingCalc')}
                  formData={formData}
                  validationErrors={validationErrors}
                  analysisType={analysisType}
                  productType="direct"
                  templateType={templateType}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onFieldKeyDown={handleFieldKeyDown}
                  onReplicate={replicateAcrossPackSizes}
                />
              </div>
              {analysisType === 'Price Change' && (
                <div ref={el => (sectionRefs.current['validation'] = el)}>
                  <FssTrackingSection
                    isExpanded={expandedSections.fssTracking}
                    onToggle={() => toggleSection('fssTracking')}
                    formData={formData}
                    onFieldChange={handleFieldChange}
                    onFieldBlur={handleFieldBlur}
                    onFieldKeyDown={handleFieldKeyDown}
                  />
                </div>
              )}
              <PackSizeOutputSection
                isExpanded={expandedSections.packSize}
                onToggle={() => toggleSection('packSize')}
                formData={formData}
                templateType={templateType}
                productType="direct"
              />
              <div ref={el => (sectionRefs.current['competitive-analysis'] = el)}>
                <CompetitiveAnalysisSection
                  isExpanded={expandedSections.competitive}
                  onToggle={() => toggleSection('competitive')}
                  bidId={bidId ? parseInt(bidId) : 0}
                  concept={formData.conceptName || ''}
                  packSize={parseFloat(formData.packSize) || 0}
                  chartDateRange={chartDateRange}
                  tempDateRange={tempDateRange}
                  calendarViewDates={calendarViewDates}
                  selectedCompany={selectedCompany}
                  isLoadingMarketTrends={isLoadingMarketTrends}
                  isLoadingMarketShare={isLoadingMarketShare}
                  volumeByCot={volumeByCot}
                  isLoadingVolumeByCot={isLoadingVolumeByCot}
                  chartOptions={marketTrendsOptions}
                  onDateRangeChange={handleDateRangeChange}
                  onApplyFilter={handleApplyDateFilter}
                  onCalendarViewDateChange={setCalendarViewDates}
                  onMarketShareClick={handleMarketShareClick}
                  onChartModalOpen={() => setShowChartModal(true)}
                />
              </div>
              <div ref={el => (sectionRefs.current['contract-price-ranking'] = el)}>
                <ContractPriceRankingSection
                  contractPriceRankingData={contractPriceRanking}
                  isExpanded={expandedSections.contractPrice}
                  onToggle={() => toggleSection('contractPrice')}
                  bidId={bidId ? parseInt(bidId) : 0}
                  concept={formData.conceptName || ''}
                  packSize={parseFloat(formData.packSize) || 0}
                  startDate={formData.usagePeriodStartDate}
                  endDate={formData.usagePeriodEndDate}
                  expandedRows={expandedRows}
                  onRowToggle={handleExpandedRowsChange}
                  isLoading={isLoadingContractPriceRanking}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onNotesChange={loadNotes}
                  notes={notes}
                  currentUser={getCurrentUser()}
                  bidOwner={bidOwner}
                />
              </div>
              <CommercialPriceRankingSection
                isExpanded={expandedSections.commercialPrice}
                onToggle={() => toggleSection('commercialPrice')}
                commercialPricing={templateData?.commercialPricing || []}
                templateType={templateType}
                formData={packSizeFormData[activePackSizeTab as number] || formData}
                onFieldChange={handleFieldChange}
                onFieldBlur={handleFieldBlur}
                onFieldKeyDown={handleFieldKeyDown}
              />
              <div ref={el => (sectionRefs.current['tmop-calculator'] = el)}>
                <TmopCalculatorSection
                  isExpanded={expandedSections.tmopCalc}
                  onToggle={() => toggleSection('tmopCalc')}
                  formData={formData}
                  validationErrors={validationErrors}
                  onFieldChange={handleFieldChange}
                  onFieldBlur={handleFieldBlur}
                  onFieldKeyDown={handleFieldKeyDown}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {templateType !== 'nc' && (
        <>
          <div ref={el => (sectionRefs.current['reports'] = el)}>
            <MASDirectGlobalOutputSection
              isExpanded={expandedSections.globalOutput}
              onToggle={() => toggleSection('globalOutput')}
              formData={formData}
              packSizeFormData={packSizeFormData}
              gsmsHndlgType={gsmsHndlgType}
              activePackSizeTab={activePackSizeTab}
              bidApprovedBy={bidDetails?.approved_by ?? null}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleFieldBlur}
              onSetActivePackSizeTab={(tabIndex: number) => {
                handlePackSizeTabChange(tabIndex);
                const conceptIndex = Math.floor(tabIndex / 1000);
                const concept = bidDetails?.form_details?.form_data?.[conceptIndex]?.concept_name;
                if (concept) {
                  setSelectedConcept(concept);
                }
              }}
              vaDataAsOf={bidDetails?.va_data_as_of ?? null}
              dodDataAsOf={bidDetails?.dod_data_as_of ?? null}
            />
          </div>
          <div>
            <WACAnalysisSection
              isExpanded={expandedSections.wacAnalysis}
              onToggle={() => toggleSection('wacAnalysis')}
              formData={formData}
              packSizeFormData={packSizeFormData}
              templateType={templateType}
            />
          </div>
        </>
      )}
    </>
  );
}
